/**
 * Translation helper — complete units, interpolation, no raw-key in production UI.
 */

import { FALLBACK_LOCALE_V0, type SupportedLocale } from './localeCanon';
import { REQUIRED_TRANSLATION_KEYS_V0, type RequiredTranslationKeyV0 } from './resources/catalog';
import { EN_MESSAGES_V0 } from './resources/en';
import { JA_JP_MESSAGES_V0 } from './resources/ja-JP';
import { KO_KR_MESSAGES_V0 } from './resources/ko-KR';

export type TranslationParamsV0 = Record<string, string | number>;

const TABLES: Record<SupportedLocale, Record<RequiredTranslationKeyV0, string>> = {
  'ko-KR': KO_KR_MESSAGES_V0,
  en: EN_MESSAGES_V0,
  'ja-JP': JA_JP_MESSAGES_V0,
};

const PLACEHOLDER_RE = /\{(\w+)\}/g;

export function listRequiredTranslationKeysV0(): readonly RequiredTranslationKeyV0[] {
  return REQUIRED_TRANSLATION_KEYS_V0;
}

export function getMessageTableV0(
  locale: SupportedLocale
): Record<RequiredTranslationKeyV0, string> {
  return TABLES[locale];
}

export function assertTranslationParityV0(): {
  ok: boolean;
  missing: Partial<Record<SupportedLocale, string[]>>;
} {
  const missing: Partial<Record<SupportedLocale, string[]>> = {};
  for (const locale of Object.keys(TABLES) as SupportedLocale[]) {
    const table = TABLES[locale];
    const gaps = REQUIRED_TRANSLATION_KEYS_V0.filter((key) => !table[key]?.trim());
    if (gaps.length) missing[locale] = gaps;
  }
  return { ok: Object.keys(missing).length === 0, missing };
}

export function extractPlaceholdersV0(template: string): string[] {
  const found: string[] = [];
  for (const match of template.matchAll(PLACEHOLDER_RE)) {
    if (match[1]) found.push(match[1]);
  }
  return found;
}

export function assertInterpolationParityV0(): {
  ok: boolean;
  mismatches: string[];
} {
  const mismatches: string[] = [];
  for (const key of REQUIRED_TRANSLATION_KEYS_V0) {
    const enPlaceholders = extractPlaceholdersV0(EN_MESSAGES_V0[key]).sort().join(',');
    for (const locale of ['ko-KR', 'ja-JP'] as const) {
      const other = extractPlaceholdersV0(TABLES[locale][key]).sort().join(',');
      if (other !== enPlaceholders) {
        mismatches.push(`${key}:${locale}`);
      }
    }
  }
  return { ok: mismatches.length === 0, mismatches };
}

function interpolate(template: string, params?: TranslationParamsV0): string {
  if (!params) return template;
  return template.replace(PLACEHOLDER_RE, (_, name: string) => {
    const value = params[name];
    return value === undefined || value === null ? `{${name}}` : String(value);
  });
}

/**
 * Translate a required key. Falls back to en, then a human-safe missing marker (never raw key alone in UI).
 */
export function tV0(
  key: RequiredTranslationKeyV0,
  locale: SupportedLocale,
  params?: TranslationParamsV0
): string {
  const primary = TABLES[locale][key];
  if (primary?.trim()) return interpolate(primary, params);
  const fallback = TABLES[FALLBACK_LOCALE_V0][key];
  if (fallback?.trim()) return interpolate(fallback, params);
  return `Missing translation (${key})`;
}

export function translateOperationStateV0(state: string, locale: SupportedLocale): string {
  const key = `states.operation.${state}` as RequiredTranslationKeyV0;
  if ((REQUIRED_TRANSLATION_KEYS_V0 as readonly string[]).includes(key)) {
    return tV0(key, locale);
  }
  return state;
}

export function translateActionKeyV0(
  action:
    | 'review'
    | 'provideInformation'
    | 'requestExecution'
    | 'approve'
    | 'deny'
    | 'start'
    | 'resume'
    | 'retry'
    | 'cancel'
    | 'requestCompensation'
    | 'openRecovery'
    | 'viewReceipt',
  locale: SupportedLocale
): string {
  return tV0(`actions.${action}`, locale);
}

/** Map purpose-centered nav areaId → translation key. */
export const NAV_AREA_TRANSLATION_KEYS_V0 = {
  today: 'navigation.today',
  workbench: 'navigation.workbench',
  operations: 'navigation.operations',
  objects: 'navigation.objects',
  evidence_decisions: 'navigation.evidenceDecisions',
  people_authority: 'navigation.peopleAuthority',
  automation: 'navigation.automation',
  configure: 'navigation.configure',
  operations_console: 'navigation.operationsConsole',
} as const;
